import React from "react";
import { Link } from "react-router-dom";
import {
  Calculator,
  HelpCircle,
  ArrowRight,
  Route as RouteIcon,
  Clock,
  Euro,
  BadgeCheck,
  MessageCircle,
  Phone,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import PointsCalculator from "@/components/PointsCalculator";
import PointsRegistrationForm from "@/components/PointsRegistrationForm";
import TrustBarPuntos from "@/components/TrustBarPuntos";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AuthorityNote } from "@/components/AuthorityNote";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { whatsappLink } from "@/lib/faro";
import { PUNTOS_EUR, CARNET_EUR } from "@/lib/courses";

const WA = whatsappLink(
  "34637570075",
  "Hola, no sé qué curso de puntos necesito y me gustaría que me asesoréis."
);

const OVERVIEW = [
  {
    tag: "Tipo A",
    title: "Curso parcial · Recuperar hasta 4 puntos",
    hours: "1 día + 3 h online",
    price: PUNTOS_EUR,
    text: "Aún tienes puntos y quieres sumar hasta 4. Un viernes en el aula + 3 h online flexibles. Sin examen.",
    href: "/recuperacion-de-puntos-vigo",
    cta: "Ver curso parcial",
  },
  {
    tag: "Tipo B1",
    title: "Curso total · Recuperar el permiso (0 puntos)",
    hours: "20 h",
    price: CARNET_EUR,
    text: "Has perdido todos los puntos (pérdida de vigencia). Con examen en la DGT al terminar.",
    href: "/curso-sensibilizacion-vigo#sin-puntos",
    cta: "Ver curso de recuperación del carnet",
  },
  {
    tag: "Tipos B2 · B3",
    title: "Recuperación por sentencia judicial",
    hours: "20 h",
    price: "consultar",
    text: "El juez te ha retirado el permiso. Según la duración de la retirada, con o sin examen DGT.",
    href: "/curso-sensibilizacion-vigo#retirada-judicial",
    cta: "Ver recuperación judicial",
  },
  {
    tag: "Ciclo adicional",
    title: "4 h para volver a examinarte",
    hours: "4 h",
    price: "consultar",
    text: "Hiciste el curso total pero suspendiste (o no te presentaste) al examen de la DGT.",
    href: "/ciclo-adicional-vigo",
    cta: "Ver ciclo adicional",
  },
];

const FAQ = [
  {
    q: "¿Cuál es la diferencia entre recuperar puntos y recuperar el carnet?",
    a: `Si aún conservas saldo de puntos y quieres sumar hasta 4, te toca el curso parcial (Tipo A · 10 h · ${PUNTOS_EUR}). Si te has quedado a 0 puntos y has recibido una carta de pérdida de vigencia, o si un juez te ha retirado el permiso, entonces necesitas el curso total de recuperación del permiso (Tipo B1 · 20 h · ${CARNET_EUR}) o el curso B2/B3 según el caso judicial.`,
  },
  {
    q: "¿Cómo sé cuántos puntos me quedan en mi permiso?",
    a: "Puedes consultarlo en cualquier momento desde la aplicación miDGT (Android e iOS) o en sede.dgt.gob.es entrando con Cl@ve, DNIe o certificado digital. También puedes llamar al 060 (información de la DGT).",
  },
  {
    q: "Me han quitado puntos por una multa. ¿Necesito hacer el curso ya?",
    a: "No es obligatorio si aún conservas saldo. El curso parcial es voluntario y solo es necesario si quieres recuperar puntos antes de tiempo. Muchos alumnos lo hacen preventivamente cuando bajan de 8 puntos para no arriesgarse a perder el permiso por una multa futura.",
  },
  {
    q: "Aún tengo puntos y quiero prevenir. ¿Puedo hacer un curso de forma voluntaria?",
    a: "Sí. El curso parcial (Tipo A) puedes hacerlo de forma voluntaria una vez cada 2 años (una vez al año si eres profesional de la conducción). Recuperas hasta 4 puntos, sin superar tu saldo máximo original.",
  },
  {
    q: "He recibido una carta de la DGT diciendo que he perdido la vigencia del permiso. ¿Qué curso me toca?",
    a: `El curso total (Tipo B1 · 20 h · ${CARNET_EUR}). Al terminar el curso, tendrás que superar un examen de control de conocimientos en la DGT (similar al teórico) para recuperar el permiso. Debes estar 6 meses sin conducir (3 meses si eres profesional) antes de poder presentarte al examen. Puedes hacer el curso durante ese periodo de espera.`,
  },
  {
    q: "Me han retirado el carnet por una sentencia judicial. ¿Qué curso necesito?",
    a: "Depende de la duración de la retirada: si es de hasta 2 años (Tipo B2), haces el curso de 20 horas y NO tienes que examinarte de nuevo en la DGT. Si es de más de 2 años (Tipo B3), haces el curso y además tienes que repetir las pruebas de la DGT. Te ayudamos con la documentación y los plazos.",
  },
  {
    q: "Hice el curso total pero suspendí el examen de la DGT. ¿Qué hago ahora?",
    a: "Antes de cada nueva convocatoria del examen tienes que hacer el ciclo adicional de 4 horas. Tienes 3 convocatorias en total. Es individual: lo programamos de un día para otro, sin esperar a formar grupo. Repasamos contigo los contenidos del examen para que apruebes esta vez.",
  },
  {
    q: "Soy conductor profesional (camión, autobús, taxi). ¿Cambia algo?",
    a: "Sí, dos cosas. Primero, puedes hacer el curso parcial una vez al año (en lugar de cada 2 años). Segundo, si te toca el curso total, el periodo de espera antes del examen es de 3 meses en lugar de 6.",
  },
  {
    q: "¿Cuánto duran cada uno de los cursos?",
    a: "Curso parcial (Tipo A): 10 horas concentradas en 1 solo día presencial (viernes 15:00–22:30 en Plaza América) + 3 h online flexibles del viernes noche al lunes noche. Curso total (Tipo B1): 20 horas — 3 días presenciales en el aula de Plaza América más 4 horas online. Ciclo adicional: 4 horas individuales, programadas a tu ritmo.",
  },
  {
    q: "¿Cuánto cuesta cada curso?",
    a: `Curso parcial: ${PUNTOS_EUR} (10 h). Curso total de recuperación del permiso: ${CARNET_EUR} (20 h). Ciclo adicional y cursos B2/B3: consulta con nosotros porque dependen del caso concreto. Todos los precios incluyen material, tasas de comunicación a la DGT y expedición del certificado.`,
  },
  {
    q: "¿Los cursos son presenciales u online?",
    a: "Mixtos. Por normativa DGT parte del curso debe ser presencial en el aula autorizada (en Plaza América, Vigo) y la parte de teleformación la haces online a tu ritmo desde tu casa. No existen cursos de recuperación 100 % online oficiales.",
  },
  {
    q: "¿Con qué frecuencia puedo hacer el curso parcial?",
    a: "Una vez cada 2 años. Si eres conductor profesional, una vez al año. Es la limitación que marca la DGT.",
  },
  {
    q: "¿Necesito examen al terminar el curso?",
    a: "Depende del curso. Parcial (Tipo A): NO hay examen — obtienes el apto por asistencia y aprovechamiento. Total (Tipo B1) y judicial B3: SÍ, examen de control de conocimientos en la DGT. Judicial B2: NO hay examen al terminar. Ciclo adicional: es la preparación previa al examen de la DGT.",
  },
  {
    q: "Si aún no lo tengo claro, ¿podéis ayudarme a decidir?",
    a: `Sí, para eso está el formulario "No lo sé, contactadme" que encontrarás al final de esta página. Nos cuentas tu situación (multas recibidas, cartas de la DGT, sentencias, etc.) y te llamamos para orientarte sin compromiso. También puedes escribirnos por WhatsApp al 637 570 075 o llamar al 986 29 24 55.`,
  },
];

export default function PuntosHubPage() {
  useSeo({
    title: "Permiso por puntos en Vigo: cómo funciona y qué curso hacer",
    description:
      `Permiso por puntos en Vigo: cómo se pierden, cómo se recuperan y qué curso necesitas: parcial (10 h · ${PUNTOS_EUR}) o total (20 h · ${CARNET_EUR}). Centro DGT.`,
    canonical: `${SITE_URL}/permiso-por-puntos-vigo`,
    ogImage: "/assets/og/curso-recuperacion-puntos-vigo.jpg",
    ogImageAlt: "Calculadora de curso de puntos — Autoescuelas Faro",
  });

  return (
    <div
      className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0"
      data-testid="puntos-hub-page"
    >
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      <Header />

      <main>
        {/* Hero */}
        <section className="relative bg-[var(--faro-bg)] pt-8 pb-10 sm:pt-12 sm:pb-14">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <Breadcrumbs
              items={[{ label: "Permiso por Puntos" }]}
            />
            <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-red)]">
              <Calculator size={14} /> Calculadora
            </div>
            <h1
              className="font-display font-black tracking-tighter text-[var(--faro-text)] mt-3 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
              data-testid="puntos-hub-h1"
            >
              Permiso por puntos en Vigo: qué curso necesitas
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[var(--faro-text-soft)] max-w-3xl leading-relaxed">
              El permiso por puntos empieza con 12 puntos (8 si eres conductor novel) y cada infracción grave o muy grave te quita entre 2 y 6. Para recuperarlos hay cuatro situaciones distintas y a cada una le corresponde un
              curso diferente. Responde a una pregunta y te enseñamos el que te
              toca, con horario, precio y reserva online. Si no lo tienes claro,
              al final de la página puedes escribirnos.
            </p>

            <div className="mt-6">
              <TrustBarPuntos />
            </div>
          </div>
        </section>

        {/* Overview de los 4 cursos */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-blue)]">
            <RouteIcon size={14} /> Los 4 caminos
          </div>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-[var(--faro-text)] leading-tight">
            Los 4 cursos del permiso por puntos en Vigo
          </h2>
          <div
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="puntos-hub-overview"
          >
            {OVERVIEW.map((c) => (
              <Link
                key={c.tag}
                to={c.href}
                data-testid={`overview-card-${c.tag.toLowerCase().replace(/\s|·/g, "-")}`}
                className="group flex flex-col gap-3 rounded-2xl border border-[rgba(30,42,143,0.15)] bg-white p-5 hover:border-[var(--faro-blue)] hover:shadow-lg hover:shadow-[rgba(30,42,143,0.08)] transition-all"
              >
                <span className="self-start inline-block text-[10px] font-black uppercase tracking-[0.18em] bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] px-2.5 py-1 rounded-full">
                  {c.tag}
                </span>
                <h3 className="font-display font-black text-lg text-[var(--faro-text)] leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[var(--faro-text-soft)] leading-relaxed">
                  {c.text}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs font-bold text-[var(--faro-blue)]">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={12} /> {c.hours}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Euro size={12} /> {c.price}
                  </span>
                </div>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-black text-[var(--faro-blue-deep)] group-hover:gap-2 transition-all">
                  {c.cta} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section id="como-funciona" className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12" data-testid="puntos-hub-explicacion"><div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-blue)]"><BadgeCheck size={14} /> Guía rápida</div><h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-[var(--faro-text)] leading-tight">¿Cómo funciona el permiso por puntos?</h2><p className="mt-4 text-[var(--faro-text-soft)] leading-relaxed">Todos los conductores empiezan con 12 puntos. Si eres conductor novel (menos de 3 años de carnet) o acabas de recuperar el permiso, empiezas con 8 y pasas a 12 tras 2 años sin perder puntos. Si conduces 3 años seguidos sin infracciones que resten puntos, ganas 2 más (14) y, con otros 3 años limpios, 1 más, hasta un máximo de 15.</p><h2 className="mt-10 font-display font-black text-2xl sm:text-3xl text-[var(--faro-text)] leading-tight">¿Cómo se pierden los puntos?</h2><p className="mt-4 text-[var(--faro-text-soft)] leading-relaxed">Solo restan puntos las infracciones graves y muy graves, y solo cuando la sanción es firme. Los casos más habituales:</p><ul className="mt-3 space-y-2 text-[var(--faro-text-soft)] leading-relaxed list-disc pl-5"><li><strong className="text-[var(--faro-text)]">6 puntos:</strong> alcohol por encima de 0,50 mg/l en aire espirado (0,30 si eres novel o profesional), conducir con drogas en el organismo o usar el móvil sujetándolo con la mano.</li><li><strong className="text-[var(--faro-text)]">4 puntos:</strong> alcohol entre 0,25 y 0,50 mg/l (0,15 a 0,30 noveles y profesionales), no llevar cinturón, casco o sillita infantil, o saltarse un semáforo en rojo o un stop.</li><li><strong className="text-[var(--faro-text)]">De 2 a 6 puntos:</strong> exceso de velocidad, según cuánto superes el límite.</li></ul><p className="mt-3 text-[var(--faro-text-soft)] leading-relaxed">Tienes la tabla completa en nuestra <Link to="/blog/permiso-por-puntos-como-funciona" className="font-bold text-[var(--faro-blue-deep)] underline">guía del permiso por puntos</Link>. Tu saldo lo puedes consultar en la app miDGT o en sede.dgt.gob.es.</p><h2 className="mt-10 font-display font-black text-2xl sm:text-3xl text-[var(--faro-text)] leading-tight">¿Cómo recuperar los puntos del carnet?</h2><p className="mt-4 text-[var(--faro-text-soft)] leading-relaxed">Hay dos caminos. <strong className="text-[var(--faro-text)]">Por tiempo:</strong> si pasan 2 años sin sanciones firmes que te quiten puntos, recuperas el saldo de 12 (3 años si alguna infracción fue muy grave). <strong className="text-[var(--faro-text)]">Con un curso:</strong> el <Link to="/recuperacion-de-puntos-vigo" className="font-bold text-[var(--faro-blue-deep)] underline">curso de recuperación de puntos en Vigo ({PUNTOS_EUR})</Link> te devuelve hasta 4 puntos en 10 horas: un viernes en el aula de Plaza América y 3 h online. Puedes hacerlo una vez cada 2 años (cada año si eres conductor profesional) y no tiene examen.</p><h2 className="mt-10 font-display font-black text-2xl sm:text-3xl text-[var(--faro-text)] leading-tight">¿Qué pasa si pierdo todos los puntos?</h2><p className="mt-4 text-[var(--faro-text-soft)] leading-relaxed">Al llegar a 0 la DGT te notifica la pérdida de vigencia del permiso y no puedes conducir durante 6 meses (3 si eres conductor profesional). Si vuelve a pasarte en los 3 años siguientes, el plazo sube a 12 meses (6 para profesionales). Para volver a conducir tienes que hacer el <Link to="/curso-sensibilizacion-vigo#sin-puntos" className="font-bold text-[var(--faro-blue-deep)] underline">curso para recuperar el carnet en Vigo (20 h · {CARNET_EUR})</Link> y aprobar un examen en la DGT. El curso lo puedes hacer mientras cumples el plazo, y vuelves a la carretera con 8 puntos. Si suspendes el examen, antes de repetirlo haces el <Link to="/ciclo-adicional-vigo" className="font-bold text-[var(--faro-blue-deep)] underline">ciclo adicional de 4 h</Link>.</p><h2 className="mt-10 font-display font-black text-2xl sm:text-3xl text-[var(--faro-text)] leading-tight">Comparativa de los cursos de puntos en Vigo</h2><div className="mt-4 overflow-x-auto rounded-2xl border border-[rgba(30,42,143,0.15)] bg-white"><table className="w-full text-sm text-left" data-testid="puntos-hub-tabla"><thead className="bg-[var(--faro-bg)] text-[var(--faro-text)]"><tr><th scope="col" className="p-3 font-black">Curso</th><th scope="col" className="p-3 font-black">Cuándo te toca</th><th scope="col" className="p-3 font-black">Duración</th><th scope="col" className="p-3 font-black">Precio</th><th scope="col" className="p-3 font-black">Examen DGT</th></tr></thead><tbody className="divide-y divide-[rgba(30,42,143,0.12)] text-[var(--faro-text-soft)]"><tr><th scope="row" className="p-3 font-bold text-[var(--faro-text)]">Parcial (Tipo A)</th><td className="p-3">Aún tienes puntos y quieres sumar hasta 4</td><td className="p-3">10 h (1 viernes + 3 h online)</td><td className="p-3">{PUNTOS_EUR}</td><td className="p-3">No</td></tr><tr><th scope="row" className="p-3 font-bold text-[var(--faro-text)]">Total (Tipo B1)</th><td className="p-3">Te has quedado a 0 puntos</td><td className="p-3">20 h</td><td className="p-3">{CARNET_EUR}</td><td className="p-3">Sí</td></tr><tr><th scope="row" className="p-3 font-bold text-[var(--faro-text)]">Judicial (B2)</th><td className="p-3">Retirada judicial de hasta 2 años</td><td className="p-3">20 h</td><td className="p-3">Consultar</td><td className="p-3">No</td></tr><tr><th scope="row" className="p-3 font-bold text-[var(--faro-text)]">Judicial (B3)</th><td className="p-3">Retirada judicial de más de 2 años</td><td className="p-3">20 h</td><td className="p-3">Consultar</td><td className="p-3">Sí</td></tr><tr><th scope="row" className="p-3 font-bold text-[var(--faro-text)]">Ciclo adicional</th><td className="p-3">Suspendiste el examen tras el curso total</td><td className="p-3">4 h</td><td className="p-3">Consultar</td><td className="p-3">Es la preparación</td></tr></tbody></table></div><h2 className="mt-10 font-display font-black text-2xl sm:text-3xl text-[var(--faro-text)] leading-tight">Calculadora: ¿qué curso de puntos necesito?</h2><p className="mt-3 text-[var(--faro-text-soft)] leading-relaxed">Si no sabes en qué caso estás, responde a la pregunta de abajo y te decimos el curso, el horario y el precio.</p></section>{/* Calculadora */}
        <section
          id="calculadora"
          className="max-w-6xl mx-auto px-5 sm:px-8 pb-10"
          data-testid="puntos-hub-calc-wrapper"
        >
          <PointsCalculator />
        </section>

        {/* FAQ */}
        <section
          id="faq"
          className="bg-[var(--faro-bg)] py-12 sm:py-16"
          data-testid="puntos-hub-faq"
        >
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-red)]">
              <HelpCircle size={14} /> Preguntas frecuentes para decidir
            </div>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-[var(--faro-text)] leading-tight">
              Todo lo que necesitas saber antes de matricularte
            </h2>
            <div className="mt-6 divide-y divide-[rgba(30,42,143,0.12)] rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white">
              {FAQ.map((f, i) => (
                <details
                  key={f.q}
                  className="group p-5 sm:p-6"
                  data-testid={`puntos-hub-faq-${i}`}
                  {...(i === 0 ? { open: true } : {})}
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="font-display font-black text-base sm:text-lg text-[var(--faro-text)] leading-snug">
                      {f.q}
                    </h3>
                    <span className="mt-1 text-[var(--faro-blue)] group-open:rotate-45 transition-transform text-xl leading-none select-none">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[var(--faro-text-soft)] leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Formulario "no lo sé" */}
        <section
          id="no-lo-se"
          className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16"
          data-testid="puntos-hub-no-lo-se"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-red)]">
                <MessageCircle size={14} /> No lo sé, contactadme
              </div>
              <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-[var(--faro-text)] leading-tight">
                ¿Aún no lo tienes claro? Te ayudamos a decidir
              </h2>
              <p className="mt-4 text-[var(--faro-text-soft)] leading-relaxed">
                Cuéntanos tu situación: qué multas te han puesto, si has
                recibido carta de la DGT, si hay sentencia judicial de por
                medio, o si te has quedado sin saber cuál te toca. Nuestro
                equipo del centro de Plaza América (autorizado DGT nº 360024)
                te llama para asesorarte sin compromiso.
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href={WA}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="puntos-hub-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-black px-4 py-3 text-sm transition-all"
                >
                  <MessageCircle size={16} /> WhatsApp 637 570 075
                </a>
                <a
                  href="tel:+34986292455"
                  data-testid="puntos-hub-cta-call"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[var(--faro-blue-deep)] text-[var(--faro-blue-deep)] font-black px-4 py-3 text-sm transition-all hover:bg-[var(--faro-blue-deep)] hover:text-white"
                >
                  <Phone size={16} /> Llamar 986 29 24 55
                </a>
              </div>
              <div className="mt-6">
                <AuthorityNote />
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-[var(--faro-text-soft)]">
                <BadgeCheck size={16} className="text-[var(--faro-blue)]" />
                Centro autorizado DGT · nº 360024 · Plaza América, Vigo
              </div>
            </div>

            <div className="lg:col-span-7">
              <PointsRegistrationForm
                defaultCourseType="NO_SABE"
                sideTitle="Cuéntanos tu situación y te llamamos"
                formId="puntos-hub-formulario"
                contextType="hub"
              />
            </div>
          </div>
        </section>
      </main>

      <FloatingWhatsapp />
      <Footer />
      <MobileBottomNav />
    </div>
  );
}
